/* Prospects (#/prospects, rail item 00) — the BizDev pre-client pipeline.
 *
 * Per anatomy §2.2, a prospect is a pre-client record: before signature the
 * PLATFORM owns the prospect identity, entities, research and opportunity
 * portfolio; after signature the linked Jira TC becomes the read-only source of
 * truth and the record continues as an active client. NO Jira connector is wired
 * and no prospect data source exists, so this stays a DESIGNED HONEST STATE.
 *
 * Upgrade over the old bare card (build brief W3-G item 3): the register's
 * grammar — the portfolio head + the empty client-book TABLE shell (its column
 * header row) with an honest banner where prospect rows would be. This reads as
 * "the same client-record grammar as your book" (§2.2), consistent with
 * views/register.ts. NO fabricated prospect rows — nothing is invented here.
 *
 * Lit conventions follow views/wiki.ts's convention header (import from 'lit';
 * render(html`…`, el)). It is a static honest surface — no fetch, no state.
 */
import { html, render, type TemplateResult } from 'lit';

function view(): TemplateResult {
  return html`
    <div class="portfolio-head">
      <div>
        <span class="eyebrow">00 · prospects · bizdev pipeline</span>
        <h1>Prospects</h1>
        <p>Pre-client records — the same client-record grammar as your book. The platform owns a prospect's
          identity, research and opportunity portfolio before signature; after the handoff its Jira TC becomes the
          read-only source of truth and it continues as an active client.</p>
      </div>
    </div>
    <div class="client-register view-table prospect-register" role="table" aria-label="Prospect book">
      <div class="register-header" role="row">
        <span role="columnheader">Prospect</span>
        <span role="columnheader">Stage</span>
        <span role="columnheader">AI-narrative</span>
        <span role="columnheader">Owner</span>
        <span role="columnheader">Jump in</span>
      </div>
      <div class="prospects-empty">
        <p class="prospects-empty-line"><b>No prospects mirrored yet.</b> Prospects mirror from Jira once the
          connector is authorized — the BizDev handoff lands here.</p>
        <p class="prospects-empty-sub">Before signature the platform is authoritative for the prospect identity,
          entities, research and opportunity portfolio; after signature the linked Jira TC is the read-only source
          of truth for contract, scope and lifecycle. No prospect rows are fabricated — this fills with real records
          when the connector is wired.</p>
      </div>
    </div>`;
}

/** Paint the prospects surface into #prospectsView. Called on navigation into
 * #/prospects and on a direct arrival (main.ts). Static — idempotent, no fetch. */
export function renderProspects(): void {
  const host = document.getElementById('prospectsView');
  if (host) render(view(), host);
}
